import axiosAPI from "axios";
import { AUTH_APP_ID, AUTH_KEY, AUTH_URL } from "../credentials";
import { APIErrorResponse } from "../interfaces/api";
import AuthResponse from "../interfaces/user";
import { getLabel } from "../hooks/use-labels";
import globalRouter from "./globalRouter";
import { showErrorNotification } from "./common-utils";
import { logoutUser } from "../store/user/user-reducer";
import { store } from "../store";

const getOptions = (providedHeaders?: object) => {
  return {
    headers: {
      "x-api-key": AUTH_KEY(),
      "x-app-id": AUTH_APP_ID(),
      ...providedHeaders,
    },
  };
};

const prepareURL = (url: string) => {
  return `${AUTH_URL()}${url}`;
};

const extractAxiosError = (errorResponse: any) => {
  const { response } = errorResponse;
  let errorObject = {} as APIErrorResponse;
  if (response && response.data) {
    const { data } = response;
    errorObject.error_code = data.error_code;
    errorObject.message = data.message;
    errorObject = {
      ...errorObject,
      ...(data.additional_data && { additional_data: data.additional_data }),
    };

    return errorObject;
  }

  return null;
};

const handleAxiosError = (error: any) => {
  const axiosError = extractAxiosError(error);
  if (axiosError) {
    return Promise.reject(axiosError);
  }

  return Promise.reject({
    error_code: "GENERAL",
    message: "Something went wrong!",
  });
};

const axios = {
  post: (url: string, data: object, headers?: object) =>
    axiosAPI.post(`${prepareURL(url)}`, data, getOptions(headers)).catch(handleAxiosError),
  get: (url: string, headers?: object) =>
    axiosAPI.get(`${prepareURL(url)}`, getOptions(headers)).catch(handleAxiosError),
  put: (url: string, data: object, headers?: object) =>
    axiosAPI.put(`${prepareURL(url)}`, data, getOptions(headers)).catch(handleAxiosError),
  delete: (url: string) =>
    axiosAPI.delete(`${prepareURL(url)}`, getOptions()).catch(handleAxiosError),
};

const loginAgainError = getLabel("loginAgainError");

export const refreshAuthToken = async () => {
  try {
    const user = localStorage.getItem("activeUser");
    if (user && user.length) {
      const loggedInUser = JSON.parse(user);
      const response = await axios.get("/auth/token", {
        Authorization: `Bearer ${loggedInUser.refresh_token}`,
      });

      if (response.data) {
        localStorage.setItem("activeUser", JSON.stringify(response.data));
        return response.data as AuthResponse;
      } else {
        logoutOnError(loginAgainError);
      }
    }
    return null;
  } catch (error) {
    console.error(error);
    logoutOnError(loginAgainError);
    return null;
  }
};

export const logoutOnError = (message?: string) => {
  store.dispatch(logoutUser());
  if (globalRouter.navigate) {
    globalRouter.navigate("/login", { replace: true });
  } else {
    window.location.replace("/login");
  }

  if (message) {
    showErrorNotification(message);
  }
};

export default axios;
